import { FrequentRiderPoints, FrequentRiderProgram } from "@graphql/api";
import { formatWithLocale, formatTime } from "@hudson/ui/lib/date_fns_locale";
import useConfig from "@lib/use_config";
import useContent from "@lib/use_content";

interface TransactionTableProps {
  points: FrequentRiderPoints[];
  programs: FrequentRiderProgram[];
}

export default function TransactionTable({ points, programs }: TransactionTableProps) {
  const { data } = useConfig(["timeFormat"]);
  const { data: contentData, loading: contentLoading } = useContent([
    "beProfilePointsTableExpiresPrefix",
    "beProfilePointsTableResidPrefix",
    "beProfilePointsTransactionsTitle",
  ]);

  const transactions = points
    .flatMap((point) => point.transactions ?? [])
    .sort((a, b) => (b.transactionDate ?? "").localeCompare(a.transactionDate ?? ""))
    .map((point) => {
      const program = programs.find((p) => Number(p.id) === point.programId);
      return {
        id: point?.id,
        date: point.transactionDate,
        comments: point.comments,
        program: program?.name || "",
        description: point.description,
        resId: point.resId == 0 ? "" : point.resId,
        points: point.debitCredit == "C" ? `+${point.points}` : `-${point.points}`,
        // Expiry lives per points lot, and only earned (credit) lots expire —
        // Bridge stamps endDate on a debit (redemption) too, which is not an
        // expiry, so gate on the credit flag. Deliberately expirationTod
        // ONLY, never a fallback to endDate: on the one real Bridge sample
        // available, endDate predates the transaction itself (not a usable
        // expiry), so a blank expirationTod renders no label rather than a
        // guessed, possibly-wrong date (Adam, 2026-09-10).
        expires:
          point.debitCredit == "C" ? formatWithLocale(point.expirationTod || undefined, "P") : "",
      };
    });

  return (
    <div>
      <h3 className="text-lg leading-6 font-semibold text-gray-900 mt-8">
        {contentData?.content.beProfilePointsTransactionsTitle}
      </h3>

      <div className="mt-3 flow-root">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <table className="min-w-full divide-y divide-gray-300 border-gray-300">
              <tbody className="divide-y divide-gray-200 bg-white">
                {transactions.map((transaction) => (
                  <tr key={transaction.id} className="even:bg-gray-50">
                    <td className="whitespace-nowrap py-2 pl-4 pr-3 text-sm text-gray-500 sm:pl-0">
                      {formatWithLocale(transaction.date, "P")}{" "}
                      {formatTime(transaction.date, data?.config.timeFormat)}
                    </td>
                    <td className="whitespace-nowrap px-2 py-2 text-sm text-gray-500">
                      {transaction.program}
                    </td>
                    <td className="whitespace-nowrap px-2 py-2 text-sm text-gray-900">
                      {transaction.description}
                    </td>
                    <td className="whitespace-nowrap px-2 py-2 text-sm font-medium text-gray-900">
                      {transaction.points}
                      {transaction.expires && (
                        // A second line under the amount, not a column: only earned
                        // rows carry it, and a sparse column gaps every other row.
                        <div className="text-xs font-normal text-gray-500">
                          {`${contentData?.content.beProfilePointsTableExpiresPrefix ?? ""} ${transaction.expires}`}
                        </div>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-2 py-2 text-sm  text-gray-500 text-left">
                      {transaction.resId
                        ? `${contentData?.content.beProfilePointsTableResidPrefix} ${transaction.resId}`
                        : ""}
                    </td>
                    <td className="whitespace-nowrap px-2 py-2 text-sm text-gray-500">
                      {transaction.comments}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
